import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  ArrowBack,
  ArrowForward,
  Chat as ChatIcon,
  Code as CodeIcon,
  CropSquare as CropSquareIcon,
  Description as DescriptionIcon,
  Html as HtmlIcon,
  InfoOutlined,
  IntegrationInstructions as IntegrationInstructionsIcon,
  Language as LanguageIcon,
  PhoneAndroid,
  Php as PhpIcon,
  Route as RouteIcon,
  ShoppingBag as ShoppingBagIcon,
  SportsEsports as SportsEsportsIcon,
  Storefront as StorefrontIcon,
  AutoAwesome as AutoAwesomeIcon,
  SmartToy as SmartToyIcon,
  Videocam as VideocamIcon,
  ViewQuilt as ViewQuiltIcon,
  VpnKey as VpnKeyIcon,
  Web,
  WebAsset as WebAssetIcon,
  Widgets as WidgetsIcon,
} from '@mui/icons-material';
import { themeBackgrounds } from '../../theme/theme';
import codeData from '../../data/getStartedCodeOptions.json';
import noCodeData from '../../data/getStartedNoCodeOptions.json';
import { SDKOption } from '../../types/documentation';
import {
  addCustomKitVariant,
  CustomKitVariant,
  getAllCustomKitVariants,
} from '../../services/customKitVariantService';

type ProductType = 'calls' | 'chat' | 'bots';
type IntegrationKitType = 'sdk' | 'nocode';

const PRODUCT_TYPES: ProductType[] = ['calls', 'chat', 'bots'];
const DEFAULT_PRODUCT: ProductType = 'calls';

const getProductTitle = (product: ProductType): string => {
  switch (product) {
    case 'calls':
      return 'Calls';
    case 'chat':
      return 'Chat & Messaging';
    case 'bots':
      return 'Chat Bot / AI Bot';
    default:
      return 'Product';
  }
};

const getProductLabel = (product: ProductType): string => {
  switch (product) {
    case 'calls':
      return 'Calls';
    case 'chat':
      return 'Chat';
    case 'bots':
      return 'Chat Bot / AI Bot';
    default:
      return 'Product';
  }
};
const DEFAULT_KIT: IntegrationKitType = 'sdk';
const DEFAULT_CODE_PLATFORM = 'react';
const DEFAULT_NO_CODE_PLATFORM = 'wordpress';

type PlatformVisual = {
  icon: React.ReactElement;
  tint: string;
  ink: string;
};

const platformVisualMap: Record<string, PlatformVisual> = {
  react: { icon: <Web />, tint: '#E0F2FE', ink: '#0369A1' },
  'react-router': { icon: <RouteIcon />, tint: '#FEE2E2', ink: '#B91C1C' },
  nextjs: { icon: <IntegrationInstructionsIcon />, tint: '#F1F5F9', ink: '#0F172A' },
  'react-native': { icon: <PhoneAndroid />, tint: '#DBEAFE', ink: '#1D4ED8' },
  flutter: { icon: <ViewQuiltIcon />, tint: '#E0F2FE', ink: '#0277BD' },
  ios: { icon: <AppleIcon />, tint: '#F1F5F9', ink: '#0F172A' },
  android: { icon: <AndroidIcon />, tint: '#DCFCE7', ink: '#15803D' },
  angular: { icon: <AccountTreeIcon />, tint: '#FEE2E2', ink: '#DC2626' },
  vue: { icon: <WidgetsIcon />, tint: '#DCFCE7', ink: '#15803D' },
  javascript: { icon: <CodeIcon />, tint: '#FEF3C7', ink: '#B45309' },
  ionic: { icon: <WebAssetIcon />, tint: '#DBEAFE', ink: '#2563EB' },
  php: { icon: <PhpIcon />, tint: '#E0E7FF', ink: '#4338CA' },
  laravel: { icon: <LanguageIcon />, tint: '#FEE2E2', ink: '#B91C1C' },
  unreal: { icon: <SportsEsportsIcon />, tint: '#E2E8F0', ink: '#0F172A' },
  wordpress: { icon: <LanguageIcon />, tint: '#DBEAFE', ink: '#1D4ED8' },
  html: { icon: <HtmlIcon />, tint: '#FFEDD5', ink: '#C2410C' },
  shopify: { icon: <ShoppingBagIcon />, tint: '#DCFCE7', ink: '#047857' },
  webflow: { icon: <WebAssetIcon />, tint: '#E0E7FF', ink: '#4338CA' },
  wix: { icon: <StorefrontIcon />, tint: '#F3E8FF', ink: '#6B21A8' },
  squarespace: { icon: <CropSquareIcon />, tint: '#E2E8F0', ink: '#0F172A' },
  others: { icon: <CodeIcon />, tint: '#F1F5F9', ink: '#334155' },
};

type CodeIntegrationOption = {
  id: string;
  title: string;
  badge?: string;
  description: string;
  buttonLabel: string;
  docsKit?: 'uikit' | 'sdk';
  docsPlatform?: string;
  docsPath?: {
    calls?: string;
    chat?: string;
    bots?: string;
  };
  linkInternal?: boolean;
};

type CodePlatformOption = SDKOption & {
  integrations?: CodeIntegrationOption[];
};

const getOptionsForKit = (kit: IntegrationKitType): SDKOption[] => {
  if (kit === 'sdk') {
    return ((codeData.options || []) as CodePlatformOption[]).map(({ id, name }) => ({ id, name }));
  }
  return (noCodeData.options || []) as SDKOption[];
};

const fillTemplate = (text: string, values: Record<string, string>): string =>
  text.replace(/\{\{(\w+)\}\}/g, (_, key: string) => values[key] ?? '');

const resolveIntegrationDocsRoute = (
  integration: CodeIntegrationOption,
  productType: ProductType
): string => {
  const explicitPath = integration.docsPath?.[productType];
  if (explicitPath) return explicitPath;

  const docsKit = integration.docsKit || integration.id;
  const docsPlatform = integration.docsPlatform || 'javascript';
  return `/documentation/${docsKit}/${productType}/${docsPlatform}`;
};

const integrationVisuals: Record<
  string,
  { icon: React.ReactElement; accent: string; softBg: string }
> = {
  uikit: {
    icon: <WidgetsIcon />,
    accent: '#007AFF',
    softBg: 'linear-gradient(135deg, rgba(0,122,255,0.08) 0%, rgba(95,176,255,0.14) 100%)',
  },
  sdk: {
    icon: <CodeIcon />,
    accent: '#0F172A',
    softBg: 'linear-gradient(135deg, rgba(15,23,42,0.06) 0%, rgba(51,65,85,0.1) 100%)',
  },
  'chat-bot': {
    icon: <ChatIcon />,
    accent: '#007AFF',
    softBg: 'linear-gradient(135deg, rgba(0,122,255,0.08) 0%, rgba(95,176,255,0.14) 100%)',
  },
  'ai-bot': {
    icon: <AutoAwesomeIcon />,
    accent: '#7C3AED',
    softBg: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(167,139,250,0.14) 100%)',
  },
};

const getDefaultPlatformForKit = (kit: IntegrationKitType, options: SDKOption[]): string => {
  const preferred = kit === 'sdk' ? DEFAULT_CODE_PLATFORM : DEFAULT_NO_CODE_PLATFORM;
  if (options.some((option) => option.id === preferred)) {
    return preferred;
  }
  return options[0]?.id || preferred;
};

const resolveKitType = (kitTypeParam?: string): IntegrationKitType => {
  if (kitTypeParam === 'sdk') return 'sdk';
  if (kitTypeParam === 'nocode' || kitTypeParam === 'uikit') return 'nocode';
  return DEFAULT_KIT;
};

const StepNumber: React.FC<{ value: number }> = ({ value }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      minWidth: 36,
      borderRadius: '50%',
      background: themeBackgrounds.default,
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: 15,
      boxShadow: '0 8px 18px rgba(0, 122, 255, 0.28)',
    }}
  >
    {value}
  </Box>
);

const getMainScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) return null;

  const marked = document.querySelector('[data-main-scroll="true"]');
  if (marked instanceof HTMLElement) return marked;

  const main = element.closest('main');
  if (main) {
    const candidates = Array.from(main.querySelectorAll<HTMLElement>('*'));
    const scrollable = candidates.find((node) => {
      const { overflowY } = window.getComputedStyle(node);
      return (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight + 1
      );
    });
    if (scrollable) return scrollable;
  }

  return null;
};

const scrollElementToTop = (element: HTMLElement | null, offset = 16) => {
  if (!element) return;

  const container = getMainScrollContainer(element);
  if (!container) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  const top =
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop -
    offset;

  container.scrollTo({
    top: Math.max(0, top),
    behavior: 'smooth',
  });
};

const GetStartedIntegrate: React.FC = () => {
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const { product: productParam, kitType: kitTypeParam, sdk: sdkParam } = useParams<{
    product?: string;
    kitType?: string;
    sdk?: string;
  }>();

  const product: ProductType = PRODUCT_TYPES.includes(productParam as ProductType)
    ? (productParam as ProductType)
    : DEFAULT_PRODUCT;

  const kitType = resolveKitType(kitTypeParam);

  const platformOptions = useMemo((): SDKOption[] => getOptionsForKit(kitType), [kitType]);

  const selectedSdk = useMemo(() => {
    if (sdkParam && platformOptions.some((option) => option.id === sdkParam)) {
      return sdkParam;
    }
    return getDefaultPlatformForKit(kitType, platformOptions);
  }, [sdkParam, platformOptions, kitType]);

  const [integrationMethod, setIntegrationMethod] = useState<'code' | 'no-code'>(
    kitType === 'sdk' ? 'code' : 'no-code'
  );
  const [contentKey, setContentKey] = useState(`${kitType}-${selectedSdk}-${product}`);
  const [variants, setVariants] = useState<CustomKitVariant[]>([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [isAddingVariant, setIsAddingVariant] = useState(false);
  const step2Ref = useRef<HTMLDivElement | null>(null);
  const shouldScrollToStep2Ref = useRef(false);
  const scrollTimersRef = useRef<number[]>([]);

  const clearScrollTimers = () => {
    scrollTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    scrollTimersRef.current = [];
  };

  const scrollToStep2Section = () => {
    scrollElementToTop(step2Ref.current, 20);
  };

  const requestScrollToStep2 = () => {
    shouldScrollToStep2Ref.current = true;
    clearScrollTimers();

    // Wait for route/content paint, then scroll the main layout container.
    scrollTimersRef.current = [50, 150].map((delay, index, list) =>
      window.setTimeout(() => {
        if (!shouldScrollToStep2Ref.current) return;
        scrollToStep2Section();
        if (index === list.length - 1) {
          shouldScrollToStep2Ref.current = false;
        }
      }, delay)
    );
  };

  useEffect(() => {
    setIntegrationMethod(kitType === 'sdk' ? 'code' : 'no-code');
  }, [kitType]);

  useEffect(() => {
    setContentKey(`${kitType}-${selectedSdk}-${product}`);
  }, [kitType, selectedSdk, product]);

  useEffect(() => {
    if (!shouldScrollToStep2Ref.current) return;
    requestScrollToStep2();
    return clearScrollTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSdk, contentKey]);

  const buildRoute = (
    nextProduct: ProductType,
    nextKit: IntegrationKitType,
    nextSdk: string
  ): string => `/get-started/${nextProduct}/${nextKit}/${nextSdk}`;

  useEffect(() => {
    const isValidProduct = PRODUCT_TYPES.includes(productParam as ProductType);
    const isValidKit = kitTypeParam === 'sdk' || kitTypeParam === 'nocode';
    const isValidSdk = Boolean(sdkParam && platformOptions.some((option) => option.id === sdkParam));

    if (!isValidProduct || !isValidKit || !isValidSdk) {
      navigate(buildRoute(product, kitType, selectedSdk), { replace: true });
    }
  }, [productParam, kitTypeParam, sdkParam, product, kitType, selectedSdk, platformOptions, navigate]);

  const selectedPlatformName =
    platformOptions.find((option) => option.id === selectedSdk)?.name || selectedSdk;

  const productTitle = getProductTitle(product);
  const productLabel = getProductLabel(product);
  const isBotVariant = product === 'bots';

  const fetchVariants = useCallback(async () => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = appId || '';

    if (kitType !== 'nocode' || !organizationId || !applicationId) {
      setVariants([]);
      setIsLoadingVariants(false);
      return;
    }

    setIsLoadingVariants(true);
    try {
      const list = await getAllCustomKitVariants(organizationId, applicationId);
      setVariants(list);
    } catch (error) {
      console.error('Error fetching custom kit variants:', error);
      toast.error('Failed to load variants. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setVariants([]);
    } finally {
      setIsLoadingVariants(false);
    }
  }, [kitType, userDetails?.organization_id, appId]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  const handleAddVariant = async () => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = appId || '';

    if (!organizationId || !applicationId) {
      toast.error('Please select an application before adding a variant.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsAddingVariant(true);
    try {
      const created = await addCustomKitVariant({
        organization_id: organizationId,
        application_id: applicationId,
        is_bot_varient: isBotVariant,
        platform_type: 'nocode',
        platform_name: selectedPlatformName,
      });
      if (!created) {
        toast.error('Failed to add variant. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      toast.success('Variant added successfully.', {
        position: 'top-right',
        autoClose: 2500,
      });
      await fetchVariants();
    } catch (error) {
      console.error('Error adding custom kit variant:', error);
      toast.error('Failed to add variant. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsAddingVariant(false);
    }
  };

  const selectedCodePlatform = useMemo((): CodePlatformOption | null => {
    if (kitType !== 'sdk') return null;
    return (
      ((codeData.options || []) as CodePlatformOption[]).find((option) => option.id === selectedSdk) ||
      null
    );
  }, [kitType, selectedSdk]);

  const codeDocOptions = useMemo(() => {
    const integrations: CodeIntegrationOption[] =
      product === 'bots'
        ? (((codeData as any).botIntegrations || []) as CodeIntegrationOption[])
        : selectedCodePlatform?.integrations || [];

    return integrations.map((integration) => {
      const visual = integrationVisuals[integration.id] || integrationVisuals.sdk;
      return {
        id: integration.id,
        title: integration.title,
        badge: integration.badge,
        description: fillTemplate(integration.description, {
          platform: selectedPlatformName,
          product: productLabel.toLowerCase(),
        }),
        buttonLabel: integration.buttonLabel,
        route: resolveIntegrationDocsRoute(integration, product),
        icon: visual.icon,
        accent: visual.accent,
        softBg: visual.softBg,
      };
    });
  }, [selectedCodePlatform, selectedPlatformName, productLabel, product]);

  const docsRoute =
    product === 'bots'
      ? codeDocOptions[0]?.route || '/chat-bot'
      : kitType === 'nocode'
        ? `/documentation/uikit/${product === 'calls' ? 'calls' : 'chat'}/javascript`
        : codeDocOptions[0]?.route || `/documentation/sdk/${product}/javascript`;

  const selectProduct = (value: ProductType) => {
    if (value === product) return;
    const nextOptions = getOptionsForKit(kitType);
    const nextSdk = nextOptions.some((option) => option.id === selectedSdk)
      ? selectedSdk
      : getDefaultPlatformForKit(kitType, nextOptions);
    navigate(buildRoute(value, kitType, nextSdk));
  };

  const changeKitType = (nextKit: IntegrationKitType) => {
    if (nextKit === kitType) return;
    const nextOptions = getOptionsForKit(nextKit);
    navigate(buildRoute(product, nextKit, getDefaultPlatformForKit(nextKit, nextOptions)));
  };

  const handleMethodChange = (_: React.MouseEvent<HTMLElement>, value: 'code' | 'no-code' | null) => {
    if (!value) return;
    changeKitType(value === 'code' ? 'sdk' : 'nocode');
  };

  const handlePlatformSelect = (sdkId: string) => {
    if (sdkId !== selectedSdk) {
      shouldScrollToStep2Ref.current = true;
      navigate(buildRoute(product, kitType, sdkId));
      return;
    }

    // Same option re-selected: still move scroller to Step 2.
    requestScrollToStep2();
  };

  const productCards: Array<{
    value: ProductType;
    title: string;
    description: string;
    icon: React.ReactElement;
  }> = [
    {
      value: 'calls',
      title: 'Calls',
      description: 'Audio & video calling for your product',
      icon: <VideocamIcon />,
    },
    {
      value: 'chat',
      title: 'Chat',
      description: 'Realtime messaging experiences',
      icon: <ChatIcon />,
    },
    {
      value: 'bots',
      title: 'Chat Bot / AI Bot',
      description: 'Chat Bots and AI-powered bots',
      icon: <SmartToyIcon />,
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1180,
        mx: 'auto',
        minWidth: 0,
        boxSizing: 'border-box',
        textAlign: 'left',
        position: 'relative',
        overflow: 'visible',
        '@keyframes fadeUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: { xs: 180, md: 220 },
          background:
            'radial-gradient(circle at 15% 20%, rgba(0, 122, 255, 0.12), transparent 45%), radial-gradient(circle at 85% 10%, rgba(95, 176, 255, 0.14), transparent 40%)',
          zIndex: 0,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, minWidth: 0, maxWidth: '100%' }}>
        <Box
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 2,
            background: themeBackgrounds.default,
            color: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 0,
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              minHeight: 40,
              px: 6,
            }}
          >
            <Button
              onClick={() => navigate(-1)}
              aria-label="Go back"
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: 40,
                width: 40,
                height: 40,
                p: 0,
                borderRadius: '50%',
                color: 'inherit',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <ArrowBack />
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                letterSpacing: '-0.025em',
              }}
            >
              {`Integrate ${productTitle}`}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              fontSize: '1.1rem',
              lineHeight: 1.6,
              mt: 1,
              mx: 'auto',
              maxWidth: 720,
            }}
          >
            Pick a product, choose Code or No Code, select your platform, and follow a guided setup.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ width: '100%', mt: 2 }}
          >
            <Chip
              label={`Get Started / ${productLabel}`}
              size="small"
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'center' },
                backgroundColor: 'rgba(255,255,255,0.22)',
                color: '#fff',
                fontWeight: 600,
                backdropFilter: 'blur(6px)',
              }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                variant="outlined"
                startIcon={<VpnKeyIcon />}
                onClick={() => navigate('/api-keys')}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.7)',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                  },
                }}
              >
                App Credentials
              </Button>
              <Button
                variant="contained"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate(docsRoute)}
                sx={{
                  backgroundColor: '#0F172A',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#1E293B' },
                }}
              >
                Documentation
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            border: '1px solid rgba(226, 232, 240, 0.95)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, #FFFFFF 100%)',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.06)',
            animation: 'fadeUp 420ms ease both',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: '#64748B', fontWeight: 700, letterSpacing: 1.1, display: 'block', mb: 1.5 }}
          >
            Choose product
          </Typography>

          <Grid2 container spacing={1.5} sx={{ mb: 3.5 }}>
            {productCards.map((item) => {
              const selected = product === item.value;
              return (
                <Grid2 key={item.value} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    onClick={() => selectProduct(item.value)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2.5,
                      p: 2.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.75,
                      border: '1.5px solid',
                      borderColor: selected ? '#007AFF' : '#E2E8F0',
                      background: selected
                        ? 'linear-gradient(135deg, rgba(0,122,255,0.08) 0%, rgba(95,176,255,0.12) 100%)'
                        : '#FFFFFF',
                      boxShadow: selected
                        ? '0 12px 28px rgba(0, 122, 255, 0.14)'
                        : '0 1px 2px rgba(15, 23, 42, 0.04)',
                      transition: 'box-shadow 180ms ease, border-color 180ms ease, background 180ms ease',
                      '&:hover': {
                        borderColor: selected ? '#007AFF' : '#BFDBFE',
                        boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        minWidth: 48,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: selected ? themeBackgrounds.default : '#F1F5F9',
                        color: selected ? '#fff' : '#334155',
                        boxShadow: selected ? '0 8px 16px rgba(0, 122, 255, 0.25)' : 'none',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: '1.05rem' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25 }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>

          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.25 }}>
            <StepNumber value={1} />
            <Box sx={{ pt: 0.35 }}>
              <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: '1.2rem', lineHeight: 1.35 }}>
                Add {productLabel} to your frontend
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                {kitType === 'nocode'
                  ? 'Select your preferred no-code platform or site builder.'
                  : 'Select the platform that best matches your tech stack.'}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ pl: { xs: 0, md: '52px' } }}>
            <ToggleButtonGroup
              exclusive
              value={integrationMethod}
              onChange={handleMethodChange}
              sx={{
                mb: 2.5,
                p: 0.5,
                borderRadius: 999,
                backgroundColor: '#F1F5F9',
                border: '1px solid #E2E8F0',
                '& .MuiToggleButtonGroup-grouped': { border: 0, margin: 0 },
                '& .MuiToggleButton-root': {
                  border: 0,
                  borderRadius: '999px !important',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#64748B',
                  gap: 0.75,
                  transition: 'all 180ms ease',
                  '&.Mui-selected': {
                    background: themeBackgrounds.default,
                    color: '#fff',
                    boxShadow: '0 8px 18px rgba(0, 122, 255, 0.28)',
                    '&:hover': { background: themeBackgrounds.default },
                  },
                },
              }}
            >
              <ToggleButton value="code">
                <CodeIcon sx={{ fontSize: 18 }} />
                Code
              </ToggleButton>
              <ToggleButton value="no-code">
                <WidgetsIcon sx={{ fontSize: 18 }} />
                No Code
              </ToggleButton>
            </ToggleButtonGroup>

            <Fade in key={`platforms-${kitType}`} timeout={280}>
              <Grid2 container spacing={1.5} alignItems="stretch">
                {platformOptions.map((option, index) => {
                  const selected = option.id === selectedSdk;
                  const visual = platformVisualMap[option.id] || platformVisualMap.others;
                  return (
                    <Grid2 key={option.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <Box
                        onClick={() => handlePlatformSelect(option.id)}
                        sx={{
                          height: '100%',
                          cursor: 'pointer',
                          borderRadius: 2.5,
                          px: 2,
                          py: 1.85,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: '1.5px solid',
                          borderColor: selected ? '#007AFF' : '#E2E8F0',
                          backgroundColor: selected ? 'rgba(0, 122, 255, 0.04)' : '#FFFFFF',
                          boxShadow: selected
                            ? '0 0 0 3px rgba(0, 122, 255, 0.12), 0 12px 24px rgba(0, 122, 255, 0.12)'
                            : '0 1px 2px rgba(15, 23, 42, 0.03)',
                          transition: 'box-shadow 180ms ease, border-color 180ms ease',
                          animation: `fadeUp 380ms ease ${index * 35}ms both`,
                          '&:hover': {
                            borderColor: selected ? '#007AFF' : '#93C5FD',
                            boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: visual.tint,
                            color: visual.ink,
                            border: '1px solid rgba(15, 23, 42, 0.04)',
                          }}
                        >
                          {visual.icon}
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                          {option.name}
                        </Typography>
                      </Box>
                    </Grid2>
                  );
                })}
              </Grid2>
            </Fade>

            <Alert
              severity="info"
              icon={<InfoOutlined />}
              sx={{
                mt: 2.75,
                borderRadius: 2.5,
                alignItems: 'flex-start',
                background:
                  'linear-gradient(135deg, rgba(224, 242, 254, 0.95) 0%, rgba(239, 246, 255, 0.95) 100%)',
                color: '#0C4A6E',
                border: '1px solid #BAE6FD',
                '& .MuiAlert-icon': { color: '#0284C7', mt: '2px' },
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              {kitType === 'sdk' ? (
                <>
                  Building on WordPress, Shopify, Webflow, or another site builder? Switch to{' '}
                  <MuiLink
                    component="button"
                    onClick={() => changeKitType('nocode')}
                    sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                  >
                    No Code
                  </MuiLink>{' '}
                  for embed-based setup.
                </>
              ) : (
                <>
                  Need a custom app integration for React, React Native, or JavaScript? Switch to{' '}
                  <MuiLink
                    component="button"
                    onClick={() => changeKitType('sdk')}
                    sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                  >
                    Code
                  </MuiLink>{' '}
                  for SDK setup.
                </>
              )}
            </Alert>
          </Box>
        </Paper>

        <Box
          ref={step2Ref}
          id="get-started-step-2"
          sx={{ scrollMarginTop: 20 }}
        >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            border: '1px solid rgba(226, 232, 240, 0.95)',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.06)',
            animation: 'fadeUp 520ms ease both',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.75 }}>
            <StepNumber value={2} />
            <Box sx={{ pt: 0.35, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: '1.2rem', lineHeight: 1.35 }}>
                {kitType === 'sdk'
                  ? product === 'bots'
                    ? `Choose bot type for ${selectedPlatformName}`
                    : `Choose integration type for ${selectedPlatformName}`
                  : `Variants for ${selectedPlatformName}`}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                {kitType === 'sdk'
                  ? product === 'bots'
                    ? `Select Chat Bot or AI Bot setup for ${selectedPlatformName}.`
                    : `Select UI Kit or SDK documentation for ${productLabel.toLowerCase()} on ${selectedPlatformName}.`
                  : `Create and manage No Code variants for ${productLabel} on ${selectedPlatformName}.`}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ pl: { xs: 0, md: '52px' } }}>
            <Fade in key={contentKey} timeout={300}>
              <Box>
                {kitType === 'sdk' ? (
                  codeDocOptions.length > 0 ? (
                    <Grid2 container spacing={2}>
                      {codeDocOptions.map((option) => (
                        <Grid2
                          key={option.id}
                          size={{ xs: 12, md: codeDocOptions.length > 1 ? 6 : 12 }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              p: 2.5,
                              borderRadius: 2.5,
                              border: '1px solid #E2E8F0',
                              background: option.softBg,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.5,
                              transition: 'box-shadow 180ms ease, border-color 180ms ease',
                              '&:hover': {
                                borderColor: option.accent,
                                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#FFFFFF',
                                  color: option.accent,
                                  border: '1px solid rgba(15, 23, 42, 0.06)',
                                }}
                              >
                                {option.icon}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.1rem' }}>
                                  {option.title}
                                </Typography>
                                {option.badge && (
                                  <Chip
                                    size="small"
                                    label={option.badge}
                                    sx={{
                                      mt: 0.5,
                                      height: 22,
                                      fontWeight: 700,
                                      backgroundColor: '#FFFFFF',
                                      color: option.accent,
                                    }}
                                  />
                                )}
                              </Box>
                            </Stack>

                            <Typography
                              variant="body2"
                              sx={{ color: '#475569', lineHeight: 1.7, flexGrow: 1 }}
                            >
                              {option.description}
                            </Typography>

                            <Button
                              variant="contained"
                              endIcon={<ArrowForward />}
                              onClick={() => navigate(option.route)}
                              sx={{
                                mt: 0.5,
                                alignSelf: 'flex-start',
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: option.accent,
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: option.accent,
                                  opacity: 0.92,
                                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.18)',
                                },
                              }}
                            >
                              {option.buttonLabel}
                            </Button>
                          </Box>
                        </Grid2>
                      ))}
                    </Grid2>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                      No UI Kit or SDK documentation options are configured for {selectedPlatformName}.
                    </Alert>
                  )
                ) : (
                  <Stack spacing={2.25} alignItems="stretch">
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        Add a variant for {selectedPlatformName}, then customize it from the list.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={
                          isAddingVariant ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <AddIcon />
                          )
                        }
                        onClick={handleAddVariant}
                        disabled={isAddingVariant || isLoadingVariants}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          background: themeBackgrounds.default,
                          alignSelf: { xs: 'stretch', sm: 'center' },
                          '&:hover': {
                            background: themeBackgrounds.default,
                            opacity: 0.92,
                          },
                        }}
                      >
                        Add Variant
                      </Button>
                    </Stack>

                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
                        overflow: 'hidden',
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ background: themeBackgrounds.default }}>
                            <TableCell
                              sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                background: themeBackgrounds.default,
                              }}
                            >
                              Variant ID
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                background: themeBackgrounds.default,
                              }}
                            >
                              Platform Name
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                background: themeBackgrounds.default,
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {isLoadingVariants ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                <CircularProgress size={28} sx={{ color: '#007AFF' }} />
                              </TableCell>
                            </TableRow>
                          ) : variants.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                <Typography variant="body2" sx={{ color: '#64748B' }}>
                                  No variants yet. Click Add Variant to create one.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            variants.map((variant) => (
                              <TableRow
                                key={variant.varient_id || variant._id}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 122, 255, 0.04)',
                                  },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 600, color: '#0F172A' }}>
                                  {variant.varient_id}
                                </TableCell>
                                <TableCell sx={{ color: '#334155' }}>
                                  {variant.platform_name || selectedPlatformName}
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      navigate(`/widget-builder/${variant.varient_id}`, {
                                        state: { variant },
                                      })
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 700,
                                      borderColor: '#007AFF',
                                      color: '#007AFF',
                                      '&:hover': {
                                        borderColor: '#0056CC',
                                        backgroundColor: 'rgba(0, 122, 255, 0.06)',
                                      },
                                    }}
                                  >
                                    Customize
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                )}
              </Box>
            </Fade>
          </Box>
        </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default GetStartedIntegrate;
